import type {
  ComponentPropsWithoutRef,
  ComponentPropsWithRef,
  ElementType,
  ReactNode,
} from 'react';

import { useMemo } from 'react';
import { twMerge } from 'tailwind-merge';

import { getTestProps } from '../../lib/helpers';

import {
  colorClasses,
  disabledClass,
  iconColorClasses,
  iconPositionClasses,
  iconPositionSpacingClasses,
  paddingClasses,
  shadowClasses,
  sizeClasses,
  sizeIconClasses,
} from './ui/classes';

export type ButtonColor = keyof typeof colorClasses;
export type ButtonSize = keyof typeof sizeClasses;
export type ButtonIconPosition = keyof typeof iconPositionClasses;

export interface ButtonBaseProps<T extends ElementType> {
  /**
   * Forwarded ref to the button element
   */
  ref?: ComponentPropsWithRef<T>['ref'];
  /**
   * Button should be displayed as
   */
  as?: T;
  /**
   * Button contents
   */
  children?: ReactNode;
  /**
   * Optional click handler
   */
  onClick?: ComponentPropsWithoutRef<T>['onClick'];
  /**
   * Is buton disabled
   */
  disabled?: boolean;
  /**
   * Button color
   */
  buttonColor?: ButtonColor;
  /**
   * Button size
   */
  buttonSize?: ButtonSize;
  /**
   * Button type
   */
  type?: 'button' | 'submit' | 'reset';
  /**
   * Button drop shadow
   */
  buttonShadow?: keyof typeof shadowClasses;
  /**
   * Button icon image
   */
  iconImage?: ReactNode;
  /**
   * Button icon color
   */
  iconColor?: keyof typeof iconColorClasses;
  /**
   * Button icon position
   */
  iconPosition?: ButtonIconPosition;
  /**
   * Button additional CSS classes
   */
  additionalClasses?: string;
  /**
   * Children additional CSS classes
   */
  additionalChildrenClasses?: string;
  /**
   * Button icon position
   */
  additionalIconClasses?: string;
  /**
   * Test id for button
   */
  testId?: string;
}

export type ButtonProps<T extends ElementType = 'button'> = ButtonBaseProps<T> &
  Omit<ComponentPropsWithoutRef<T>, keyof ButtonBaseProps<T>>;

const Button = <T extends ElementType = 'button'>({
  ref,
  as,
  children,
  onClick,
  disabled = false,
  buttonSize = 'base',
  buttonColor = 'blue',
  buttonShadow = 'none',
  type = 'button',
  iconImage,
  iconColor = 'inherit',
  iconPosition = 'end',
  additionalClasses = '',
  additionalIconClasses = '',
  additionalChildrenClasses = '',
  testId = '',
  ...props
}: ButtonProps<T>) => {
  const filteredProps = useMemo(() => {
    const { className: _, ...rest } = props;
    return rest;
  }, [props]);

  const ButtonComponent = as || 'button';

  const buttonProps = ButtonComponent === 'button' ? { type } : {};

  return (
    <ButtonComponent
      ref={ref}
      className={twMerge(
        'relative group/button flex justify-between items-center w-fit',
        'transition duration-200 ease-in-out',
        'leading-none rounded-lg font-medium border-2',
        'focus:outline-hidden cursor-pointer whitespace-nowrap',
        sizeClasses[buttonSize],
        shadowClasses[buttonShadow],
        colorClasses[buttonColor],
        iconPositionClasses[iconPosition],
        buttonColor === 'borderless' || buttonColor === 'borderlessBlue'
          ? 'p-0'
          : paddingClasses[buttonSize],
        !children && 'justify-center',
        additionalClasses,
        disabled && disabledClass,
      )}
      onClick={onClick}
      disabled={disabled}
      {...filteredProps}
      {...buttonProps}
      {...getTestProps(testId)}
    >
      <span
        className={twMerge(additionalChildrenClasses)}
        {...getTestProps(testId, 'children')}
      >
        {children}
      </span>

      {iconImage && (
        <div
          className={twMerge(
            'flex items-center justify-center bg-transparent',
            sizeIconClasses[buttonSize],
            iconColorClasses[iconColor],
            children && iconPositionSpacingClasses[iconPosition],
            !children &&
              'group-focus-visible/button:outline-2 group-focus-visible/button:outline-offset-4 ' +
                'group-focus-visible/button:outline-blue',
            additionalIconClasses,
          )}
          {...getTestProps(testId, 'icon')}
        >
          {iconImage}
        </div>
      )}
    </ButtonComponent>
  );
};

export default Button;
